/**
 * Family Service
 * 家庭代碼共享功能
 * - createFamilyCode / getUserProfile / updateMemberNickname：直接寫 Firestore
 * - joinFamily / leaveFamily / revokeAccess：透過 Cloud Functions（安全驗證）
 */

import { CapacitorHttp } from '@capacitor/core'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import { auth } from '../firebase'
import { getDocument, setDocument, mergeDocument, updateDocument, deleteDocument } from './firestoreService'
import type { UserProfile } from '../types'

const CF_BASE = 'https://us-central1-babyvaccinehelper.cloudfunctions.net'

// 判斷平台
const isCapacitor = typeof window !== 'undefined' && (window as any).Capacitor !== undefined
const platform = isCapacitor ? (window as any).Capacitor?.getPlatform?.() : 'web'
const useNativeAuth = isCapacitor && platform === 'ios'

/**
 * 呼叫 Cloud Function（HTTP onRequest 版本）
 * 使用 X-Firebase-ID-Token 自訂標頭傳遞身份，避免 Cloud Run 基礎設施層的 Authorization 攔截
 */
async function callFunction(name: string, data: Record<string, unknown>): Promise<unknown> {
  // 取得 Firebase ID Token（iOS 用原生 API，Android 用 Web SDK）
  let idToken: string
  if (useNativeAuth) {
    const result = await FirebaseAuthentication.getIdToken()
    idToken = result.token
  } else {
    idToken = (await auth.currentUser?.getIdToken()) ?? ''
  }
  if (!idToken) throw Object.assign(new Error('請先登入'), { code: 'unauthenticated' })

  const response = await CapacitorHttp.post({
    url: `${CF_BASE}/${name}`,
    headers: {
      'Content-Type': 'application/json',
      'X-Firebase-ID-Token': idToken,
    },
    data,
  })

  const json = response.data
  if (response.status >= 400 || json?.error) {
    const code = (json?.error?.status ?? 'UNKNOWN').toLowerCase().replace(/_/g, '-')
    throw Object.assign(new Error(json?.error?.message ?? '請求失敗'), { code: `functions/${code}` })
  }
  return json?.result
}

// ==================== Pure Helpers ====================

/**
 * 產生 6 位大寫英數字家庭代碼
 */
export function generateFamilyCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' // 排除易混淆字元 0/O/1/I
  let code = ''
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

// ==================== Firestore Direct ====================

/**
 * 建立家庭代碼（擁有者呼叫）
 * 寫入 familyCodes/{code} 和 users/{uid} root document
 */
export async function createFamilyCode(uid: string, code: string, familyName?: string): Promise<void> {
  // 讀取舊代碼（只取 familyCode，用於刪除）
  const profile = await getUserProfile(uid)
  if (profile.familyCode && profile.familyCode !== code) {
    await deleteDocument(`familyCodes/${profile.familyCode}`).catch(() => {})
  }

  // 寫入新代碼 lookup table
  await setDocument(`familyCodes/${code}`, {
    ownerUid: uid,
    createdAt: new Date().toISOString(),
  })

  // merge 寫入（doc 不存在時建立，存在時只更新指定欄位）
  // 不傳入 pendingMembers / pendingWith / sharedWith，保留既有資料
  const updates: Record<string, unknown> = { familyCode: code }
  if (familyName !== undefined) {
    updates.familyName = familyName.trim() || null
  }
  await mergeDocument(`users/${uid}`, updates)
}

/**
 * 查詢家庭代碼是否存在
 * @returns ownerUid，若不存在回傳 null
 */
export async function lookupFamilyCode(code: string): Promise<string | null> {
  const doc = await getDocument<{ ownerUid: string }>(`familyCodes/${code.toUpperCase()}`)
  return doc?.ownerUid ?? null
}

/**
 * 讀取使用者 profile（root document）
 */
export async function getUserProfile(uid: string): Promise<UserProfile> {
  const doc = await getDocument<UserProfile>(`users/${uid}`)
  return {
    familyCode: doc?.familyCode,
    familyName: doc?.familyName ?? null,
    sharedWith: (doc?.sharedWith as Record<string, any>) ?? {},
    pendingMembers: (doc?.pendingMembers as Record<string, any>) ?? {},
    linkedTo: doc?.linkedTo ?? null,
    pendingWith: doc?.pendingWith ?? null,
  }
}

/**
 * 更新家庭名稱（擁有者呼叫）
 */
export async function updateFamilyName(uid: string, familyName: string): Promise<void> {
  await updateDocument(`users/${uid}`, { familyName: familyName.trim() || null })
}

/**
 * 更新成員顯示名稱（擁有者呼叫）
 */
export async function updateMemberNickname(
  ownerUid: string,
  memberUid: string,
  nickname: string
): Promise<void> {
  await updateDocument(`users/${ownerUid}`, {
    [`sharedWith.${memberUid}.nickname`]: nickname,
  })
}

// ==================== Cloud Function Callables ====================

/**
 * 申請加入家庭（B 呼叫）
 * Cloud Function 驗證代碼並建立待審核申請（不直接加入）
 */
export async function joinFamily(code: string): Promise<{ ownerUid: string }> {
  const result = await callFunction('joinFamily', { code: code.toUpperCase() })
  return result as { ownerUid: string }
}

/**
 * 審核通過（Owner A 呼叫）
 */
export async function approveMember(memberUid: string): Promise<void> {
  await callFunction('approveMember', { memberUid })
}

/**
 * 拒絕申請（Owner A 呼叫）
 */
export async function rejectMember(memberUid: string): Promise<void> {
  await callFunction('rejectMember', { memberUid })
}

/**
 * 取消申請（B 主動取消）
 */
export async function cancelJoinRequest(): Promise<void> {
  await callFunction('cancelJoinRequest', {})
}

/**
 * 離開家庭（B 主動離開）
 * Cloud Function 移除 sharedWith 並清除 linkedTo
 */
export async function leaveFamily(ownerUid: string): Promise<void> {
  await callFunction('leaveFamily', { ownerUid })
}

/**
 * 移除成員（擁有者 A 呼叫）
 * Cloud Function 移除 sharedWith 並清除 B 的 linkedTo
 */
export async function revokeAccess(memberUid: string): Promise<void> {
  await callFunction('revokeAccess', { memberUid })
}

